import React, { useState } from 'react';
import { GetStarted } from '@questlabs/react-sdk';
import SafeIcon from '../common/SafeIcon';

const WORKFLOW_CANDIDATES = [
  { id: 1, name: 'Yaw Mensah', role: 'Operations Manager', stage: 'Technical Review', avatar: 'https://i.pravatar.cc/150?img=11', progress: 45 },
  { id: 2, name: 'Ama Serwaa', role: 'Frontend Developer', stage: 'Interviewing', avatar: 'https://i.pravatar.cc/150?img=32', progress: 70 },
  { id: 3, name: 'Ekow Thompson', stage: 'Screening', role: 'Sales Executive', avatar: 'https://i.pravatar.cc/150?img=12', progress: 15 },
  { id: 4, name: 'Linda Ofori', stage: 'Background Check', role: 'HR Specialist', avatar: 'https://i.pravatar.cc/150?img=44', progress: 85 },
];

const STAGES = [
  { name: 'Screening', icon: 'Search', color: 'bg-blue-500' },
  { name: 'Technical', icon: 'Code', color: 'bg-purple-500' },
  { name: 'Interview', icon: 'MessageSquare', color: 'bg-brand-secondary' },
  { name: 'Decision', icon: 'CheckCircle', color: 'bg-green-500' },
];

const ApplicationWorkflow = () => {
  const [selectedCandidate, setSelectedCandidate] = useState(WORKFLOW_CANDIDATES[0]);

  const styleConfig = {
    Form: {
      backgroundColor: '#FFFFFF',
      borderRadius: '12px',
      padding: '32px',
      border: '1px solid #f1f5f9',
      boxShadow: 'none'
    },
    Heading: { 
      color: '#11469A', 
      fontSize: '22px', 
      fontWeight: '800',
      marginBottom: '8px'
    },
    Description: { 
      color: '#64748b', 
      fontSize: '14px', 
      marginBottom: '32px'
    },
    Card: {
      backgroundColor: '#f8fafc',
      border: '1px solid #e2e8f0',
      borderRadius: '12px',
      padding: '20px'
    },
    PrimaryButton: {
      backgroundColor: '#11469A',
      color: '#FFFFFF',
      borderRadius: '8px',
      fontWeight: '700',
      fontSize: '13px',
      textTransform: 'uppercase',
      letterSpacing: '0.05em'
    },
    ProgressBar: {
      barColor: '#11469A',
      barParentColor: '#e2e8f0',
      ProgressText: {
        color: '#11469A',
        fontSize: '14px',
        fontWeight: '700'
      }
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Application Review Workflow</h1>
          <p className="text-gray-500">Track and advance candidates through the hiring pipeline.</p>
        </div>
        <div className="flex items-center gap-2 bg-white px-4 py-2 rounded-lg border border-gray-100 shadow-sm">
          <span className="text-xs font-bold text-gray-400 uppercase tracking-widest">Active Reviews:</span>
          <span className="text-sm font-bold text-brand-primary">24 Candidates</span>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* Left: Pipeline Queue */}
        <div className="lg:col-span-4 space-y-4">
          <div className="card overflow-hidden">
            <div className="p-4 bg-gray-50 border-b border-gray-100 flex justify-between items-center">
              <span className="font-bold text-xs uppercase tracking-widest text-gray-400">Pipeline Queue</span>
              <button className="text-[#00AEEF] hover:text-[#11469A]"><SafeIcon name="Filter" size={16} /></button>
            </div>
            <div className="divide-y divide-gray-100 max-h-[600px] overflow-y-auto scrollbar-hide">
              {WORKFLOW_CANDIDATES.map((candidate) => (
                <button 
                  key={candidate.id} 
                  onClick={() => setSelectedCandidate(candidate)}
                  className={`w-full flex items-center gap-4 p-5 text-left transition-all ${selectedCandidate.id === candidate.id ? 'bg-blue-50/50 border-r-4 border-r-[#11469A]' : 'hover:bg-gray-50'}`}
                >
                  <div className="relative">
                    <img src={candidate.avatar} className="w-12 h-12 rounded-2xl object-cover shadow-sm" alt="" />
                    <div className="absolute -bottom-1 -right-1 w-5 h-5 bg-white rounded-full flex items-center justify-center border border-gray-100 shadow-sm">
                      <div className={`w-3 h-3 rounded-full ${candidate.progress > 50 ? 'bg-green-500' : 'bg-yellow-500'}`} />
                    </div>
                  </div>
                  <div className="flex-1 min-w-0">
                    <h4 className="font-bold text-gray-800 truncate text-sm">{candidate.name}</h4>
                    <p className="text-[11px] text-[#00AEEF] font-bold truncate uppercase tracking-wider">{candidate.stage}</p>
                    <div className="mt-2 w-full bg-gray-100 h-1 rounded-full overflow-hidden">
                      <div className="bg-brand-primary h-full rounded-full" style={{ width: `${candidate.progress}%` }} />
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </div>

          <div className="card p-6 bg-brand-dark text-white relative overflow-hidden">
            <div className="absolute top-0 right-0 p-4 opacity-10">
              <SafeIcon name="TrendingUp" size={80} />
            </div>
            <div className="relative z-10">
              <h4 className="font-bold text-sm mb-2 uppercase tracking-widest">Workflow Health</h4>
              <div className="text-3xl font-bold mb-4">84%</div>
              <p className="text-xs text-white/60 leading-relaxed font-medium">
                Candidates are moving through the pipeline 12% faster than last month.
              </p>
            </div>
          </div>
        </div>

        {/* Right: Workflow Engine */}
        <div className="lg:col-span-8 space-y-6">
          <div className="card p-6 border-none shadow-sm flex flex-col md:flex-row justify-between items-start md:items-center gap-6">
            <div className="flex items-center gap-5">
              <img src={selectedCandidate.avatar} className="w-16 h-16 rounded-2xl shadow-md border-2 border-white" alt="" />
              <div>
                <p className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-1">Current Review</p>
                <h2 className="text-xl font-bold text-gray-800">{selectedCandidate.name}</h2>
                <span className="text-xs font-bold text-brand-primary bg-brand-primary/5 px-2 py-0.5 rounded uppercase tracking-wider">
                  {selectedCandidate.role}
                </span>
              </div>
            </div>
            <div className="flex gap-4">
              {STAGES.map((stage, i) => (
                <div key={i} className="flex flex-col items-center gap-1.5 opacity-40 hover:opacity-100 transition-opacity">
                  <div className={`w-8 h-8 rounded-lg ${stage.color} flex items-center justify-center text-white shadow-sm`}>
                    <SafeIcon name={stage.icon} size={14} />
                  </div>
                  <span className="text-[9px] font-bold text-gray-400 uppercase tracking-widest">{stage.name}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-white rounded-xl overflow-hidden shadow-sm border border-gray-100">
            <GetStarted 
              styleConfig={styleConfig}
              questId="hiring-workflow-quest"
              userId="admin-hr"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <button className="card p-4 flex items-center justify-between group hover:border-[#11469A] transition-all border border-transparent">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-blue-50 text-[#11469A] flex items-center justify-center group-hover:bg-[#11469A] group-hover:text-white transition-all">
                  <SafeIcon name="Clipboard" size={18} />
                </div>
                <div className="text-left">
                  <p className="text-xs font-bold text-gray-800">Review Notes</p>
                  <p className="text-[10px] text-gray-400 font-medium">Add internal commentary</p>
                </div>
              </div>
              <SafeIcon name="ChevronRight" size={16} className="text-gray-300 group-hover:text-[#11469A]" />
            </button>
            <button className="card p-4 flex items-center justify-between group hover:border-[#00AEEF] transition-all border border-transparent">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-cyan-50 text-[#00AEEF] flex items-center justify-center group-hover:bg-[#00AEEF] group-hover:text-white transition-all">
                  <SafeIcon name="Mail" size={18} />
                </div>
                <div className="text-left">
                  <p className="text-xs font-bold text-gray-800">Send Update</p>
                  <p className="text-[10px] text-gray-400 font-medium">Notify candidate of stage</p>
                </div>
              </div>
              <SafeIcon name="ChevronRight" size={16} className="text-gray-300 group-hover:text-[#00AEEF]" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ApplicationWorkflow;